import { Accessibility, Eye, Link2, RotateCcw, Type, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import "./accessibility-toolbar.css";

type ContrastMode = "default" | "grayscale" | "high" | "negative" | "light";

interface AccessibilityPreferences {
  textScale: number;
  contrast: ContrastMode;
  underlineLinks: boolean;
  readableFont: boolean;
  reduceMotion: boolean;
}

const storageKey = "pby-accessibility";
const defaultPreferences: AccessibilityPreferences = {
  textScale: 100,
  contrast: "default",
  underlineLinks: false,
  readableFont: false,
  reduceMotion: false,
};

function readPreferences(): AccessibilityPreferences {
  try {
    const saved = JSON.parse(window.localStorage.getItem(storageKey) ?? "null") as
      | Partial<AccessibilityPreferences>
      | null;
    if (!saved) return defaultPreferences;
    return {
      textScale: [90, 100, 112.5, 125, 137.5].includes(Number(saved.textScale))
        ? Number(saved.textScale)
        : 100,
      contrast: ["default", "grayscale", "high", "negative", "light"].includes(
        saved.contrast ?? "",
      )
        ? (saved.contrast as ContrastMode)
        : "default",
      underlineLinks: saved.underlineLinks === true,
      readableFont: saved.readableFont === true,
      reduceMotion: saved.reduceMotion === true,
    };
  } catch {
    return defaultPreferences;
  }
}

function applyPreferences(preferences: AccessibilityPreferences) {
  const root = document.documentElement;
  root.style.fontSize = `${preferences.textScale}%`;
  root.dataset.a11yContrast = preferences.contrast;
  root.classList.toggle("a11y-underline-links", preferences.underlineLinks);
  root.classList.toggle("a11y-readable-font", preferences.readableFont);
  root.classList.toggle("a11y-reduce-motion", preferences.reduceMotion);
}

interface AccessibilityToolbarProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function AccessibilityToolbar({
  open: controlledOpen,
  onOpenChange,
}: AccessibilityToolbarProps = {}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (onOpenChange) onOpenChange(nextOpen);
      else setInternalOpen(nextOpen);
    },
    [onOpenChange],
  );
  const [preferences, setPreferences] =
    useState<AccessibilityPreferences>(readPreferences);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    applyPreferences(preferences);
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(preferences));
    } catch {
      // Los ajustes funcionan aunque el navegador no permita guardarlos.
    }
  }, [preferences]);

  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      toggleRef.current?.focus();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open, setOpen]);

  const changeText = (direction: -1 | 1) => {
    const scales = [90, 100, 112.5, 125, 137.5];
    const current = Math.max(0, scales.indexOf(preferences.textScale));
    const next = Math.min(scales.length - 1, Math.max(0, current + direction));
    setPreferences((value) => ({ ...value, textScale: scales[next] ?? 100 }));
  };

  const setContrast = (contrast: ContrastMode) => {
    setPreferences((value) => ({
      ...value,
      contrast: value.contrast === contrast ? "default" : contrast,
    }));
  };

  const toggle = (
    key: "underlineLinks" | "readableFont" | "reduceMotion",
  ) => setPreferences((value) => ({ ...value, [key]: !value[key] }));

  return (
    <div className="accessibility-tools">
      <button
        ref={toggleRef}
        className="accessibility-tools-trigger"
        type="button"
        aria-expanded={open}
        aria-controls="accessibility-tools-panel"
        aria-label={`${open ? "Cerrar" : "Abrir"} herramientas de accesibilidad`}
        onClick={() => setOpen(!open)}
      >
        <Accessibility aria-hidden="true" />
        <span>Accesibilidad</span>
      </button>
      {open ? (
        <div
          ref={panelRef}
          className="accessibility-tools-panel"
          id="accessibility-tools-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby="accessibility-tools-title"
        >
          <header>
            <div>
              <strong id="accessibility-tools-title">Herramientas de accesibilidad</strong>
              <small>Ajustá la visualización del portal</small>
            </div>
            <button
              type="button"
              aria-label="Cerrar herramientas de accesibilidad"
              onClick={() => {
                setOpen(false);
                toggleRef.current?.focus();
              }}
            >
              <X aria-hidden="true" />
            </button>
          </header>

          <div className="accessibility-tools-grid">
            <button type="button" onClick={() => changeText(1)}>
              <Type aria-hidden="true" /> Aumentar texto
            </button>
            <button type="button" onClick={() => changeText(-1)}>
              <Type aria-hidden="true" /> Disminuir texto
            </button>
            <button
              type="button"
              aria-pressed={preferences.contrast === "grayscale"}
              onClick={() => setContrast("grayscale")}
            >
              <Eye aria-hidden="true" /> Escala de grises
            </button>
            <button
              type="button"
              aria-pressed={preferences.contrast === "high"}
              onClick={() => setContrast("high")}
            >
              <Eye aria-hidden="true" /> Alto contraste
            </button>
            <button
              type="button"
              aria-pressed={preferences.contrast === "negative"}
              onClick={() => setContrast("negative")}
            >
              <Eye aria-hidden="true" /> Contraste negativo
            </button>
            <button
              type="button"
              aria-pressed={preferences.contrast === "light"}
              onClick={() => setContrast("light")}
            >
              <Eye aria-hidden="true" /> Fondo claro
            </button>
            <button
              type="button"
              aria-pressed={preferences.underlineLinks}
              onClick={() => toggle("underlineLinks")}
            >
              <Link2 aria-hidden="true" /> Enlaces subrayados
            </button>
            <button
              type="button"
              aria-pressed={preferences.readableFont}
              onClick={() => toggle("readableFont")}
            >
              <Type aria-hidden="true" /> Fuente legible
            </button>
            <button
              type="button"
              aria-pressed={preferences.reduceMotion}
              onClick={() => toggle("reduceMotion")}
            >
              <Accessibility aria-hidden="true" /> Reducir movimiento
            </button>
            <button
              className="accessibility-tools-reset"
              type="button"
              onClick={() => setPreferences(defaultPreferences)}
            >
              <RotateCcw aria-hidden="true" /> Reiniciar
            </button>
          </div>
          <p className="sr-only" aria-live="polite">
            Tamaño de texto: {preferences.textScale} por ciento.
          </p>
        </div>
      ) : null}
    </div>
  );
}

